import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import {
  CommonActions,
  NavigationHelpers,
  ParamListBase,
  getFocusedRouteNameFromRoute,
  StackActions,
  TabNavigationState,
} from '@react-navigation/native';
import { useColorScheme } from 'nativewind';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isMobile, isMobileWeb, isTablet } from '../utils/responsive';
import { Icon } from '../components/index';
import svgIcons from '../assets/index';
import { useAdmin } from '../contexts/Admin/AdminContext';
import { ScreenName, StackName, TabNavigationEventMap, TabNavigationOptions } from './types';

const screensWithBackNavigation: ScreenName[] = [
  ScreenName.EventScreen,
  ScreenName.ArticleScreen,
  ScreenName.PersonalizationScreen,
  ScreenName.AdminArticleListScreen,
  ScreenName.AdminEventListScreen,
  ScreenName.AdminExtremeEventListScreen,
  ScreenName.AdminHelpdeskCategoriesScreen,
  ScreenName.AdminHelpdeskIssueListScreen,
];

const mainMenuScreens: StackName[] = [
  StackName.HomeStack,
  StackName.EventsStack,
  StackName.PlatformsStack,
  StackName.ArticlesStack,
  StackName.HelpdeskStack,
];

interface MainMenuProps {
  state: TabNavigationState<ParamListBase>;
  navigation: NavigationHelpers<ParamListBase, TabNavigationEventMap>;
  descriptors: Record<string, { options: TabNavigationOptions; render: () => React.ReactNode }>;
}

const MainMenu = ({ state, navigation, descriptors }: MainMenuProps): React.ReactElement => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const { colorScheme } = useColorScheme();
  const { setIsBackModalOpen, isDirty } = useAdmin();
  const focusedTab = state.routes[state.index];
  const focusedRouteName = getFocusedRouteNameFromRoute(focusedTab) ?? focusedTab.name;

  const isBackNavigation = screensWithBackNavigation.includes(focusedRouteName as ScreenName);
  const isEditScreen = !!focusedRouteName?.match(/edit/i);

  const handleGoBack = () => {
    const tabState = navigation.getState();
    const focused = tabState.routes[tabState.index];
    const nested = focused.state;

    if (nested?.key) {
      navigation.dispatch({ ...StackActions.pop(1), target: nested.key });
    } else if (nested && nested.index > 0) {
      const prev = nested.routes[nested.index - 1];
      navigation.navigate(focused.name, { screen: prev.name, params: prev.params });
    } else {
      navigation.goBack();
    }
  };

  const [backButtonHovered, setBackButtonHovered] = useState(false);
  const [smallBackHovered, setSmallBackHovered] = useState(false);
  const insets = useSafeAreaInsets();

  const getIconNameFromStackName = (stackName: StackName): string =>
    stackName.replace('Stack', '').toLowerCase();

  return (
    <View
      testID="main-menu"
      style={[
        isMobile
          ? {
              flexDirection: 'row',
              alignItems: 'flex-start',
              bottom: 0,
              zIndex: 20,
              width: '100%',
              paddingBottom: isTablet ? insets.bottom + 8 : insets.bottom,
              backgroundColor: colorScheme === 'dark' ? '#000' : '#fff',
            }
          : {
              flexDirection: 'column',
              position: 'fixed' as 'absolute',
              left: 0,
              flex: 1,
              height: '100%',
              zIndex: 20,
              width: 100,
            },
      ]}
    >
      {/* SMALL BACK BUTTON  */}
      {!isMobile ? (
        <Pressable
          style={
            [
              {
                height: isBackNavigation ? 100 : 0,
                opacity: isBackNavigation ? 1 : 0,
                backgroundColor: smallBackHovered
                  ? `${colorScheme === 'dark' ? '#272727' : '#dfdfdf'}`
                  : colorScheme === 'dark'
                    ? '#000000'
                    : '#ffffff',
                alignItems: 'center',
                justifyContent: 'center',
              },
              !isMobile ? { transition: 'all 0.3s ease-in-out' } : null,
            ] as React.ComponentProps<typeof Pressable>['style']
          }
          onHoverIn={() => setSmallBackHovered(true)}
          onHoverOut={() => setSmallBackHovered(false)}
          onPress={() => {
            handleGoBack();
            setSmallBackHovered(false);
          }}
          testID="main-menu-back-button"
        >
          <Icon
            icon={svgIcons.arrowLeftIcon}
            mobileSize={{ width: 24, height: 24 }}
            webSize={{ width: 40, height: 40 }}
          />
        </Pressable>
      ) : (
        <></>
      )}
      {state.routes.map((route, index) => {
        const isFocused = state.index === index;
        const isHovered = hoveredItem === route.key;
        const iconName = getIconNameFromStackName(route.name as StackName);
        const transitionValue = 'all 0.3s ease-in-out';
        const isMainMenu = mainMenuScreens.includes(route?.name as StackName);

        let backgroundColor: string;
        let menuButtonTextColor: string;
        // Dark theme menu button and text color
        if (colorScheme === 'dark' && !isFocused) {
          backgroundColor = isHovered || backButtonHovered ? 'rgba(246,246,246,0.1)' : '#000000';
          menuButtonTextColor = '#fff';
        } else if (colorScheme === 'dark' && isFocused) {
          backgroundColor = '#fff';
          menuButtonTextColor = '#101010';
        }

        // Light theme menu button and text color
        if (colorScheme === 'light' && !isFocused) {
          backgroundColor = isHovered || backButtonHovered ? 'rgba(16,16,16,0.1)' : '#ffffff';
          menuButtonTextColor = '#101010';
        } else if (colorScheme === 'light' && isFocused) {
          backgroundColor = '#000';
          menuButtonTextColor = '#fff';
        }

        // MAIN MENU BUTTONS
        if (!((!isEditScreen || isMobile) && isMainMenu)) {
          return null;
        }

        return (
          <View
            key={route.key}
            style={{
              flex: 1,
              // Extra background on mobile below the nav items
              backgroundColor: colorScheme === 'dark' ? '#000000' : '#ffffff',
            }}
          >
            <Pressable
              key={route.key}
              onPress={() => {
                const event = navigation.emit({
                  type: 'tabPress',
                  target: route.key,
                  canPreventDefault: true,
                  data: {
                    isAlreadyFocused: isFocused,
                  },
                } as {
                  type: 'tabPress';
                  target: string;
                  canPreventDefault: true;
                  data: { isAlreadyFocused: boolean };
                });

                if (!isFocused && !event.defaultPrevented) {
                  navigation.dispatch({
                    ...CommonActions.navigate(route),
                    target: state.key,
                  });
                }
              }}
              onPressIn={() => setHoveredItem(route.key)}
              onPressOut={() => setHoveredItem(null)}
              onHoverIn={() => setHoveredItem(route.key)}
              onHoverOut={() => setHoveredItem(null)}
              style={[
                {
                  backgroundColor: backgroundColor,
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: isTablet ? 56 : 48,
                  paddingVertical: isMobileWeb ? 10 : null,
                },
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                !isMobile || isMobileWeb ? { transition: transitionValue, flex: 1 } : null,
              ]}
            >
              <Icon
                icon={svgIcons[iconName]}
                mobileSize={{ width: 24, height: 24 }}
                webSize={{ width: 35, height: 35 }}
                isFocused={isFocused}
                testID="main-menu-icon"
              />
              <Text
                style={
                  [
                    {
                      fontSize: isTablet ? 12 : isMobile ? 10 : 12,
                      fontFamily: 'sans-400',
                      color: menuButtonTextColor,
                      textAlign: 'center',
                      fontWeight: '400',
                      textTransform: isMobile ? 'capitalize' : 'uppercase',
                      marginTop: isMobile ? 2 : 10,
                      lineHeight: isTablet ? 18 : isMobile ? 16 : 18,
                    },
                    !isMobile ? { transition: transitionValue } : null,
                  ] as Text['props']['style']
                }
              >
                {descriptors[route.key].options.title ?? route.name}
              </Text>
            </Pressable>
          </View>
        );
      })}

      {/*  BACK BUTTON  */}
      {isEditScreen && !isMobile ? (
        <Pressable
          style={
            [
              {
                flex: 1,
                backgroundColor: backButtonHovered
                  ? `${colorScheme === 'dark' ? '#272727' : '#dfdfdf'}`
                  : colorScheme === 'dark'
                    ? '#000000'
                    : '#ffffff',
                alignItems: 'center',
                justifyContent: 'center',
              },
              !isMobile ? { transition: 'all 0.3s ease-in-out' } : null,
            ] as unknown as React.ComponentProps<typeof Pressable>['style']
          }
          onHoverIn={() => setBackButtonHovered(true)}
          onHoverOut={() => setBackButtonHovered(false)}
          onPress={() => {
            if (isDirty) {
              setIsBackModalOpen(true);
            } else {
              handleGoBack();
            }
            setBackButtonHovered(false);
          }}
        >
          <Icon
            icon={svgIcons.arrowLeftIcon}
            mobileSize={{ width: 24, height: 24 }}
            webSize={{ width: 40, height: 40 }}
          />
        </Pressable>
      ) : null}

      {/*LOGO IN THE WEB MAIN MENU*/}
      {!isMobile ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colorScheme === 'dark' ? '#000000' : '#ffffff',
          }}
        >
          <Pressable
            onPress={() => {
              navigation.dispatch(CommonActions.navigate({ name: ScreenName.HomeScreen }));
            }}
            testID="main-menu-logo"
          >
            <Icon
              icon={svgIcons.logo}
              mobileSize={{ width: 84, height: 20 }}
              webSize={{ width: 65, height: 15 }}
            />
          </Pressable>
        </View>
      ) : null}
    </View>
  );
};

export default MainMenu;
